<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';

import CardLayout from 'dashboard/components-next/CardLayout.vue';
import ContactsForm from 'dashboard/components-next/Contacts/ContactsForm/ContactsForm.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Flag from 'dashboard/components-next/flag/Flag.vue';
import ContactDeleteSection from 'dashboard/components-next/Contacts/ContactsCard/ContactDeleteSection.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import countries from 'shared/constants/countries';

const props = defineProps({
  id: { type: Number, required: true },
  name: { type: String, default: '' },
  email: { type: String, default: '' },
  companyId: { type: [Number, String], default: '' },
  additionalAttributes: { type: Object, default: () => ({}) },
  customAttributes: { type: Object, default: () => ({}) },
  talkviaProfile: { type: Object, default: () => ({}) },
  talkviaProposals: { type: Object, default: () => ({}) },
  phoneNumber: { type: String, default: '' },
  thumbnail: { type: String, default: '' },
  availabilityStatus: { type: String, default: null },
  blacklisted: { type: Boolean, default: false },
  isExpanded: { type: Boolean, default: false },
  isUpdating: { type: Boolean, default: false },
  selectable: { type: Boolean, default: false },
  isSelected: { type: Boolean, default: false },
});

const emit = defineEmits([
  'toggle',
  'updateContact',
  'showContact',
  'select',
  'avatarHover',
]);

const { t } = useI18n();

const copy = {
  age: 'Idade',
  birthDate: 'Nascimento',
  motherName: 'Mãe',
  cpf: 'CPF',
  rg: 'RG',
  registration: 'Matrícula',
  margin: 'Margem',
  lastInteraction: 'Última interação',
  contracts: 'Contratos',
  inProgress: 'em andamento',
  paid: 'pagos',
  canceled: 'cancelados',
  latestContracts: 'Contratos da esteira',
  proposal: 'Proposta',
  blacklist: 'Blacklist',
  removeBlacklist: 'Remover blacklist',
};

const contactsFormRef = ref(null);

const getInitialContactData = () => ({
  id: props.id,
  name: props.name,
  email: props.email,
  companyId: props.companyId,
  phoneNumber: props.phoneNumber,
  additionalAttributes: props.additionalAttributes,
  customAttributes: props.customAttributes,
});

const contactData = ref(getInitialContactData());

const isFormInvalid = computed(() => contactsFormRef.value?.isFormInvalid);

const countriesMap = computed(() => {
  return countries.reduce((acc, country) => {
    acc[country.id] = country;
    return acc;
  }, {});
});

const countryDetails = computed(() => {
  const attributes = props.additionalAttributes || {};
  const { country, countryCode, city } = attributes;

  if (!country && !countryCode) return null;

  const activeCountry =
    countriesMap.value[country] || countriesMap.value[countryCode];

  if (!activeCountry) return null;

  return {
    countryCode: activeCountry.id,
    city: city ? `${city},` : null,
    name: activeCountry.name,
  };
});

const formattedLocation = computed(() => {
  if (!countryDetails.value) return '';

  return [countryDetails.value.city, countryDetails.value.name]
    .filter(Boolean)
    .join(' ');
});

const moneyFormatter = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
});

const formatDate = value =>
  value ? new Date(value).toLocaleDateString('pt-BR') : '-';

const formatDateTime = value =>
  value ? new Date(value).toLocaleString('pt-BR') : '-';

const formatMoney = value => {
  if (value === null || value === undefined || value === '') return '-';
  const numeric = Number(String(value).replace(/\./g, '').replace(',', '.'));
  return Number.isNaN(numeric) ? value : moneyFormatter.format(numeric);
};

const profileItems = computed(() =>
  [
    props.talkviaProfile?.age && {
      label: copy.age,
      value: props.talkviaProfile.age,
    },
    props.talkviaProfile?.birthDate && {
      label: copy.birthDate,
      value: formatDate(props.talkviaProfile.birthDate),
    },
    props.talkviaProfile?.cpf && {
      label: copy.cpf,
      value: props.talkviaProfile.cpf,
    },
    props.talkviaProfile?.registrationNumber && {
      label: copy.registration,
      value: props.talkviaProfile.registrationNumber,
    },
    props.talkviaProfile?.availableMargin && {
      label: copy.margin,
      value: formatMoney(props.talkviaProfile.availableMargin),
    },
    props.talkviaProfile?.lastInteractionAt && {
      label: copy.lastInteraction,
      value: formatDateTime(props.talkviaProfile.lastInteractionAt),
    },
  ].filter(Boolean)
);

const expandedProfileItems = computed(() =>
  [
    ...profileItems.value,
    props.talkviaProfile?.motherName && {
      label: copy.motherName,
      value: props.talkviaProfile.motherName,
    },
    props.talkviaProfile?.rg && {
      label: copy.rg,
      value: props.talkviaProfile.rg,
    },
  ].filter(Boolean)
);

const proposalSummary = computed(() => {
  const summary = props.talkviaProposals || {};
  if (!summary.total) return '';

  return `${summary.total} ${copy.contracts} · ${summary.inProgress || 0} ${
    copy.inProgress
  } · ${summary.paid || 0} ${copy.paid} · ${summary.canceled || 0} ${
    copy.canceled
  }`;
});

const latestProposals = computed(() => props.talkviaProposals?.latest || []);

const handleFormUpdate = updatedData => {
  Object.assign(contactData.value, updatedData);
};

const handleUpdateContact = () => {
  emit('updateContact', contactData.value);
};

const handleRemoveBlacklist = () => {
  emit('updateContact', { id: props.id, blacklisted: false });
};

const onClickExpand = () => {
  emit('toggle');
  contactData.value = getInitialContactData();
};

const onClickViewDetails = () => emit('showContact', props.id);

const toggleSelect = checked => {
  emit('select', checked);
};

const handleAvatarHover = isHovered => {
  emit('avatarHover', isHovered);
};
</script>

<template>
  <div class="relative">
    <CardLayout
      :key="id"
      layout="row"
      :class="{
        'outline-n-weak !bg-n-slate-3 dark:!bg-n-solid-3': isSelected,
      }"
    >
      <div class="flex items-center justify-start flex-1 gap-4">
        <div
          class="relative"
          @mouseenter="handleAvatarHover(true)"
          @mouseleave="handleAvatarHover(false)"
        >
          <Avatar
            :name="name"
            :src="thumbnail"
            :size="42"
            :status="availabilityStatus"
            hide-offline-status
          >
            <template v-if="selectable" #overlay="{ size }">
              <label
                class="flex items-center justify-center rounded-full cursor-pointer absolute inset-0 z-10 backdrop-blur-[2px] border border-n-weak"
                :style="{ width: `${size}px`, height: `${size}px` }"
                @click.stop
              >
                <Checkbox
                  :model-value="isSelected"
                  @change="event => toggleSelect(event.target.checked)"
                />
              </label>
            </template>
          </Avatar>
        </div>
        <div class="flex flex-col gap-0.5 flex-1">
          <div class="flex flex-wrap items-center gap-x-4 gap-y-1">
            <span class="text-base font-medium truncate text-n-slate-12">
              {{ name }}
            </span>
            <span
              v-if="blacklisted"
              class="inline-flex items-center gap-1 rounded-full bg-n-ruby-3 px-2 py-0.5 text-xs font-medium text-n-ruby-11"
            >
              <span class="i-lucide-shield-alert size-3" />
              {{ copy.blacklist }}
            </span>
            <span class="inline-flex items-center gap-1">
              <span
                v-if="additionalAttributes?.companyName"
                class="i-ph-building-light size-4 text-n-slate-10 mb-0.5"
              />
              <span
                v-if="additionalAttributes?.companyName"
                class="text-sm truncate text-n-slate-11"
              >
                {{ additionalAttributes.companyName }}
              </span>
            </span>
          </div>
          <div
            class="flex flex-wrap items-center justify-start gap-x-3 gap-y-1"
          >
            <div v-if="email" class="truncate max-w-72" :title="email">
              <span class="text-sm text-n-slate-11">
                {{ email }}
              </span>
            </div>
            <div v-if="email" class="w-px h-3 truncate bg-n-slate-6" />
            <span v-if="phoneNumber" class="text-sm truncate text-n-slate-11">
              {{ phoneNumber }}
            </span>
            <div v-if="phoneNumber" class="w-px h-3 truncate bg-n-slate-6" />
            <span
              v-if="countryDetails"
              class="inline-flex items-center gap-2 text-sm truncate text-n-slate-11"
            >
              <Flag :country="countryDetails.countryCode" class="size-3.5" />
              {{ formattedLocation }}
            </span>
            <div v-if="countryDetails" class="w-px h-3 truncate bg-n-slate-6" />
            <Button
              v-if="blacklisted"
              ghost
              slate
              sm
              icon="i-lucide-shield-off"
              :label="copy.removeBlacklist"
              :disabled="isUpdating"
              @click.stop="handleRemoveBlacklist"
            />
            <Button
              :label="t('CONTACTS_LAYOUT.CARD.VIEW_DETAILS')"
              variant="link"
              size="xs"
              @click="onClickViewDetails"
            />
          </div>
          <div
            v-if="profileItems.length || proposalSummary"
            class="mt-2 flex flex-wrap items-center gap-2"
          >
            <span
              v-for="item in profileItems.slice(0, 4)"
              :key="item.label"
              class="rounded-md bg-n-alpha-2 px-2 py-1 text-xs text-n-slate-11"
            >
              <strong class="font-medium text-n-slate-12">
                {{ item.label }}:
              </strong>
              {{ item.value }}
            </span>
            <span
              v-if="proposalSummary"
              class="rounded-md bg-n-blue-3 px-2 py-1 text-xs text-n-blue-11"
            >
              {{ proposalSummary }}
            </span>
          </div>
        </div>
      </div>

      <Button
        icon="i-lucide-chevron-down"
        variant="ghost"
        color="slate"
        size="xs"
        :class="{ 'rotate-180': isExpanded }"
        @click="onClickExpand"
      />

      <template #after>
        <div
          class="transition-all duration-500 ease-in-out grid overflow-hidden"
          :class="
            isExpanded
              ? 'grid-rows-[1fr] opacity-100'
              : 'grid-rows-[0fr] opacity-0'
          "
        >
          <div class="overflow-hidden">
            <div class="flex flex-col gap-6 p-6 border-t border-n-strong">
              <section
                v-if="expandedProfileItems.length || latestProposals.length"
                class="grid gap-4 rounded-lg border border-n-weak bg-n-solid-2 p-4"
              >
                <div
                  v-if="expandedProfileItems.length"
                  class="grid gap-3 sm:grid-cols-2 lg:grid-cols-4"
                >
                  <div
                    v-for="item in expandedProfileItems"
                    :key="item.label"
                    class="rounded-lg bg-n-solid-1 p-3"
                  >
                    <p class="mb-1 text-xs uppercase text-n-slate-10">
                      {{ item.label }}
                    </p>
                    <p class="mb-0 text-sm font-medium text-n-slate-12">
                      {{ item.value }}
                    </p>
                  </div>
                </div>

                <div v-if="latestProposals.length" class="grid gap-2">
                  <h4 class="mb-0 text-sm font-semibold text-n-slate-12">
                    {{ copy.latestContracts }}
                  </h4>
                  <div class="grid gap-2 lg:grid-cols-2">
                    <article
                      v-for="proposal in latestProposals"
                      :key="proposal.id"
                      class="rounded-lg border border-n-weak bg-n-solid-1 p-3"
                    >
                      <div class="mb-1 flex items-start justify-between gap-3">
                        <p
                          class="mb-0 truncate text-sm font-medium text-n-slate-12"
                        >
                          {{
                            proposal.title || `${copy.proposal} #${proposal.id}`
                          }}
                        </p>
                        <span class="text-xs text-n-slate-10">
                          #{{ proposal.id }}
                        </span>
                      </div>
                      <p class="mb-1 text-sm text-n-slate-11">
                        {{ formatMoney(proposal.amount) }} ·
                        {{ proposal.status || '-' }}
                      </p>
                      <p class="mb-0 text-xs text-n-slate-10">
                        {{ formatDateTime(proposal.updatedAt) }}
                      </p>
                    </article>
                  </div>
                </div>
              </section>
              <ContactsForm
                ref="contactsFormRef"
                :contact-data="contactData"
                @update="handleFormUpdate"
              />
              <div>
                <Button
                  :label="
                    t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.UPDATE_BUTTON')
                  "
                  size="sm"
                  :is-loading="isUpdating"
                  :disabled="isUpdating || isFormInvalid"
                  @click="handleUpdateContact"
                />
              </div>
            </div>
            <ContactDeleteSection
              :selected-contact="{
                id: props.id,
                name: props.name,
              }"
            />
          </div>
        </div>
      </template>
    </CardLayout>
  </div>
</template>
